import type { ElementType, ReactNode } from "react";

/**
 * The surface primitive (2026-09). Before this the card recipe was pasted by
 * hand more than 360 times, which is why a design change could not land in one
 * place. New surfaces use <Card>; existing ones migrate as they are touched,
 * and the design-tokens guard test holds the count of raw copies so it only
 * goes down.
 *
 * Border, fill, radius, and shadow say "separate object", so spend them by
 * role: `flat` for a card nested inside another card (border only), default
 * for a standalone card, `interactive` for a card that is itself a link.
 */

/** The canonical standalone card recipe, for the rare place a class string is needed. */
export const CARD_CLASS = "spark-card rounded-lg border border-spark-hairline bg-spark-surface shadow-[var(--shadow-card)]";
/** A card nested inside another card: hairline border, no shadow. */
export const CARD_FLAT_CLASS = "rounded-lg border border-spark-hairline bg-spark-surface";
/** Hover lift for cards that are links or buttons. Pair with focus-visible so keyboards get the same state. */
export const CARD_INTERACTIVE_CLASS =
  "transition-shadow duration-150 ease-out hover:shadow-[var(--shadow-card-hover)] focus-visible:shadow-[var(--shadow-card-hover)]";

const PADDING = { none: "", sm: "p-4", md: "p-5", lg: "p-6" } as const;

export function Card({
  as,
  flat = false,
  interactive = false,
  padding = "md",
  className,
  children,
  ...rest
}: {
  as?: ElementType;
  flat?: boolean;
  interactive?: boolean;
  padding?: keyof typeof PADDING;
  className?: string;
  children: ReactNode;
} & Record<string, unknown>) {
  const Tag = as ?? "div";
  const classes = [
    flat ? CARD_FLAT_CLASS : CARD_CLASS,
    interactive ? CARD_INTERACTIVE_CLASS : "",
    PADDING[padding],
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Tag className={classes} {...rest}>
      {children}
    </Tag>
  );
}
